/**
 * Pure upload speed/ETA math, kept dependency-light so it's unit-testable in
 * isolation (UploadManager pulls in the whole store/network graph). This is the
 * shared source of truth for the panel banner, each BatchCard, and the per-file
 * status pill. Headline surfaces use the global session counter; batch and file
 * rows use per-file windows because they need scoped estimates.
 *
 * Speed is always measured over a TIME-BOUNDED window (SPEED_WINDOW_MS). This is
 * the third point on a spectrum we've traversed twice before — don't regress to
 * either end:
 * - lifetime average (bytes ÷ since-start, removed 2026-06-17): never forgets a
 *   slow start / recovered stall ⇒ "done in about 2 h" while actually finishing.
 * - count-based recent window (last 6 part-completion samples, removed for this):
 *   20MB parts upload 8 at a time and complete in bursts, so 6 samples can span
 *   sub-second ⇒ speed spikes ~8×, then decays until the next burst ⇒ the ETA
 *   see-saws instead of counting down.
 * A window ≥ SPEED_WINDOW_MS spans several burst cycles (stable) yet ages a slow
 * start out within ~30s (fresh).
 */

export type UploadProgressSample = { t: number; progress: number };

/** One point of the session-wide bytes-uploaded counter (see UploadManager). */
export type UploadBytesSample = { t: number; bytes: number };

export type UploadStats = { speedBps: number; etaSeconds: number };

/**
 * The distinct states of the headline upload ETA, modelled explicitly rather
 * than as sentinel numbers on a single `number`.
 *
 * The previous shape overloaded one value — `-1` = not started, `Infinity` = no
 * estimate, `> 0` = a real ETA — and left every consumer to re-derive that
 * mapping by hand (`< 0` here, `!Number.isFinite` there, `> 0 && isFinite`
 * elsewhere). Collapsing two of those cases at the producer then silently broke
 * exactly one consumer: a stalled upload read "Preparing to upload…" in the
 * library header while bytes were mid-flight, because the header treated `-1`
 * as "not started" and nothing else did.
 *
 * `Infinity` was also hiding two situations that deserve different copy: an
 * upload that has not measured any throughput YET, and one that had throughput
 * and lost it. The first is normal and resolves on its own; the second means
 * the connection is dying and the user is owed a different message.
 */
export type UploadEtaState =
  /** Nothing has begun transferring yet — no file has been picked up. */
  | { kind: 'not-started' }
  /**
   * Transferring, but no usable estimate. Usually transient — nothing has moved
   * yet, or movement is too recent for the estimator — and self-resolving.
   *
   * One non-transient case also lands here, deliberately: every started file has
   * reached 100% and is waiting on its finish POST (with more files queued
   * behind). `latestTransferAdvanceMs` ignores finish-awaiting files, so there
   * is no mid-transfer clock to age and `stalled` cannot fire, however wedged
   * those finishes are. Do not treat this kind as proof that things are fine.
   *
   * Reads as plain "uploading" — never "preparing" (a file HAS been picked up)
   * and never "finishing up".
   */
  | { kind: 'warming-up' }
  /**
   * Bytes were moving and no MID-TRANSFER file has advanced for
   * `HEADLINE_STALL_AFTER_MS`. Shares its definition of a stall with the
   * per-batch detector (`isBatchStalled` — both go through `hasStalled`), but
   * NOT its population (every in-flight file vs. one batch's uuids) or its
   * threshold (the headline waits longer, so ambient chrome does not flap on a
   * bursty link). A batch card reporting a stall while the header still counts
   * down is therefore expected, not a bug.
   *
   * NOTE on rendering: no surface currently gives this its own copy. The
   * library header shows it identically to `warming-up` (neutral "Uploading N
   * files..."), so its only observable effect is that the countdown disappears.
   * The stall-specific copy ("Waiting for a stable connection…") comes from
   * `deriveBatchState` → `'stalled'`, a different signal on a different clock.
   * The kind is still modelled here so the producer states stay exhaustive and
   * un-collapsible — collapsing producer states is exactly how the original
   * "Preparing to upload…"-while-mid-flight bug shipped.
   */
  | { kind: 'stalled' }
  /** A real estimate. `seconds` is always finite and >= 0. */
  | { kind: 'estimate'; seconds: number };

/** Persistent presentation state updated only when upload bytes advance. */
export type SmoothedUploadStats = {
  completionAtMs: number;
  speedBps: number;
  updatedAtMs: number;
};

/**
 * Minimum span of the speed window, ms. Long enough to cover several
 * 8-concurrent-chunk completion bursts on typical connections; short enough that
 * a slow start stops influencing the estimate within half a minute.
 */
export const SPEED_WINDOW_MS = 30_000;

/**
 * Memory guard only — at 20MB per sample (one per completed part) this is not a
 * speed-window bound. On connections fast enough to overflow it within
 * SPEED_WINDOW_MS (>85MB/s sustained) the window shrinks, but at those speeds
 * the ETA is seconds anyway.
 */
const MAX_SAMPLES = 128;

/** Presentation EMA tuning. Capping elapsed time prevents the first callback in
 * a long-awaited burst from consuming the whole smoothing period at once. */
const ETA_SMOOTHING_TIME_CONSTANT_MS = 10_000;
const ETA_SMOOTHING_MAX_STEP_MS = 2_000;

/**
 * Fold a new progress-driven measurement into the visible completion time and
 * speed. Keeping completion as an absolute timestamp makes ETA count down
 * between part completions instead of rising while the raw speed window ages.
 */
export function updateSmoothedUploadStats(
  previous: SmoothedUploadStats | null,
  measurement: UploadStats | null,
  nowMs: number,
): SmoothedUploadStats | null {
  if (
    !measurement ||
    !Number.isFinite(measurement.etaSeconds) ||
    measurement.etaSeconds <= 0 ||
    !Number.isFinite(measurement.speedBps) ||
    measurement.speedBps <= 0
  ) {
    return previous;
  }

  const candidateCompletionAtMs = nowMs + measurement.etaSeconds * 1000;
  // After a full-window pause the old completion timestamp is no longer useful.
  // Re-seed on the first resumed byte movement so the UI leaves its stalled
  // fallback immediately instead of slowly blending out of a stale target.
  if (!previous || nowMs - previous.updatedAtMs >= SPEED_WINDOW_MS) {
    return {
      completionAtMs: candidateCompletionAtMs,
      speedBps: measurement.speedBps,
      updatedAtMs: nowMs,
    };
  }

  const elapsedMs = Math.max(0, Math.min(nowMs - previous.updatedAtMs, ETA_SMOOTHING_MAX_STEP_MS));
  const factor = 1 - Math.exp(-elapsedMs / ETA_SMOOTHING_TIME_CONSTANT_MS);
  return {
    completionAtMs:
      previous.completionAtMs + (candidateCompletionAtMs - previous.completionAtMs) * factor,
    speedBps: previous.speedBps + (measurement.speedBps - previous.speedBps) * factor,
    updatedAtMs: nowMs,
  };
}

/**
 * Read the progressively counting-down estimate. A large divergence from the
 * raw estimator means progress has genuinely stalled; hide the stale countdown
 * rather than letting it reach zero while work remains.
 */
export function readSmoothedUploadStats(
  smoothed: SmoothedUploadStats | null,
  raw: UploadStats | null,
  nowMs: number,
): UploadStats | null {
  if (!raw || !smoothed) return raw;

  const etaSeconds = Math.max(0.5, (smoothed.completionAtMs - nowMs) / 1000);
  const stallThreshold = Math.max(etaSeconds * 2, etaSeconds + SPEED_WINDOW_MS / 1000);
  if (!Number.isFinite(raw.etaSeconds) || raw.etaSeconds > stallThreshold) {
    return { speedBps: 0, etaSeconds: Infinity };
  }

  return { speedBps: smoothed.speedBps, etaSeconds };
}

/**
 * Drop samples that can no longer anchor a window: everything strictly older
 * than the newest sample that is already ≥ SPEED_WINDOW_MS old (that one is kept
 * — it defines the window start until a younger sample crosses the age line).
 */
function pruneWindowedSamples<T extends { t: number }>(samples: T[], nowMs: number): T[] {
  const cutoff = nowMs - SPEED_WINDOW_MS;
  let anchorIdx = -1;
  for (let i = samples.length - 1; i >= 0; i--) {
    if (samples[i].t <= cutoff) {
      anchorIdx = i;
      break;
    }
  }
  const pruned = anchorIdx > 0 ? samples.slice(anchorIdx) : samples;
  return pruned.length > MAX_SAMPLES ? pruned.slice(-MAX_SAMPLES) : pruned;
}

/**
 * Append a per-file progress sample, keeping the buffer monotonic and
 * age-pruned. Returns a NEW array (immutable update — plays nice with the spread
 * in updateUploadStatusWithData + MobX). Keeps the array unchanged when progress
 * didn't advance (so a stall leaves the window fixed and `nowMs` extends the
 * elapsed time); resets the window if progress went backwards (a retry
 * restarting from a lower %).
 */
export function appendProgressSample(
  existing: UploadProgressSample[] | undefined,
  progress: number,
  nowMs: number,
): UploadProgressSample[] {
  const samples = existing ?? [];
  const last = samples[samples.length - 1];
  if (last && progress < last.progress) return [{ t: nowMs, progress }];
  if (last && progress <= last.progress) return samples;
  return pruneWindowedSamples([...samples, { t: nowMs, progress }], nowMs);
}

/**
 * Append a session-counter sample. The counter is monotonic by construction
 * (UploadManager only appends on a positive byte delta), so there is no
 * regression-reset branch here — session boundaries reset the whole buffer
 * instead.
 */
export function appendBytesSample(
  existing: UploadBytesSample[] | undefined,
  bytes: number,
  nowMs: number,
): UploadBytesSample[] {
  return pruneWindowedSamples([...(existing ?? []), { t: nowMs, bytes }], nowMs);
}

/**
 * The window anchor: the newest point that is at least a full window old AND
 * still shows movement relative to the current value (during a stall the latest
 * samples equal the current value — anchoring there would freeze speed at 0
 * instantly instead of degrading it smoothly). Falls back to the oldest point
 * (session/upload start) while nothing is a full window old yet, i.e. warmup
 * uses a shorter, since-start window.
 */
function findWindowAnchor<T extends { t: number }>(
  points: T[],
  value: (p: T) => number,
  currentValue: number,
  nowMs: number,
): T | null {
  const cutoff = nowMs - SPEED_WINDOW_MS;
  for (let i = points.length - 1; i >= 0; i--) {
    const p = points[i];
    if (p.t <= cutoff && value(p) < currentValue) return p;
  }
  return points[0] ?? null;
}

/**
 * Newest wall-clock time any MID-transfer file (progress < 100) moved raw
 * bytes, or null when no such file has moved yet. The single clock behind BOTH
 * stall detectors — per-batch (`isBatchStalled`) and global (`globalUploadEta`
 * → 'stalled') — so the two can never disagree about when a stall begins. The
 * `progress < 100` filter is load-bearing: a
 * file at 100% is awaiting its finish POST — permanently quiet by design — so
 * its aging clock says nothing about the connection. Counting it made a
 * healthy batch whose started files had all finished transferring read
 * "Waiting for a stable connection…" and offer Cancel against in-flight
 * non-idempotent finishes.
 */
export function latestTransferAdvanceMs(
  files: { progress: number; lastProgressAtMs?: number }[],
): number | null {
  let latest: number | null = null;
  for (const f of files) {
    if (f.lastProgressAtMs == null || (f.progress || 0) >= 100) continue;
    if (latest == null || f.lastProgressAtMs > latest) latest = f.lastProgressAtMs;
  }
  return latest;
}

/** Minimal shape needed to estimate — FileUploadStatus is structurally assignable. */
type UploadStatsInput = {
  file?: { size?: number } | null;
  progress: number;
  uploadStartedAt?: number;
  /** Progress at `uploadStartedAt`: 0 for a fresh upload, the parts an earlier
   * attempt already landed for a resumed one. Undefined reads as 0. */
  resumedProgress?: number;
  progressSamples?: UploadProgressSample[];
};

/**
 * Aggregates per-file windowed speed into a {speedBps, etaSeconds} pair for the
 * supplied files (one file = the per-file pill, many = a BatchCard).
 *
 * Per-file caveats (why the global banner uses computeGlobalUploadStats
 * instead): a completed file leaves the caller's file set, so its recent bytes
 * vanish from the sum (ETA blips up until siblings' windows absorb the freed
 * bandwidth), and a single-part file (≤20MB) contributes no speed until the
 * moment it completes. Both are tolerable on a per-file pill / batch line,
 * not on the headline countdown.
 *
 * Stall behaviour: no new samples ⇒ `nowMs` keeps extending the window ⇒ speed
 * degrades smoothly toward 0 / ETA → ∞. Callers pass `nowMs` (typically
 * `mobxNow(500)`) so wall-clock reactivity stays at the call site. Returns null
 * when none of the supplied files have started uploading.
 */
export function computeBytesBasedUploadStats(
  files: UploadStatsInput[],
  nowMs: number,
): UploadStats | null {
  if (files.length === 0) return null;

  let totalSpeedBps = 0;
  let totalRemainingBytes = 0;
  let activeFileCount = 0;

  for (const f of files) {
    const fileSize = f.file?.size || 0;
    if (fileSize === 0) continue;

    totalRemainingBytes += (1 - f.progress / 100) * fileSize;

    // Anchor candidates: the upload's start plus the recorded samples, oldest →
    // newest. Null ⇒ still queued, no clock yet. A resumed upload starts at the
    // share it already had, which this attempt did not move.
    const points: UploadProgressSample[] = [
      ...(f.uploadStartedAt != null
        ? [{ t: f.uploadStartedAt, progress: f.resumedProgress ?? 0 }]
        : []),
      ...(f.progressSamples ?? []),
    ];
    const anchor = findWindowAnchor(points, (p) => p.progress, f.progress, nowMs);
    if (!anchor) continue;

    const elapsedSec = (nowMs - anchor.t) / 1000;
    if (elapsedSec <= 0) continue;

    // Bytes moved across the window. Can be 0 (first part not in yet, or a stall)
    // — still count the file as "active" so the surface stays visible; speed sits
    // at 0 and the ETA degrades as elapsed grows.
    const windowBytes = ((f.progress - anchor.progress) / 100) * fileSize;
    totalSpeedBps += Math.max(0, windowBytes) / elapsedSec;
    activeFileCount++;
  }

  if (activeFileCount === 0) return null;

  return {
    speedBps: totalSpeedBps,
    etaSeconds: totalSpeedBps > 0 ? totalRemainingBytes / totalSpeedBps : Infinity,
  };
}

/**
 * Session-wide speed/ETA for the headline surfaces (panel banner, library
 * header). Speed comes from the monotonic session byte counter — NOT from
 * summing per-file windows — so it has none of the per-file caveats:
 * - a file completing keeps its recent bytes in the counter (no ETA blip up);
 * - single-part files register their bytes the moment they land (a batch of
 *   small files gets a real speed instead of a permanent ∞ ETA).
 *
 * `files` (the queued+uploading set) still supplies the remaining-bytes total
 * and the null gate; `bytesUploaded`/`bytesSamples` are the session counter and
 * its sample history (seeded with a {t, bytes: 0} sample at session start).
 * Returns null when none of the supplied files have started uploading.
 */
export function computeGlobalUploadStats(
  files: UploadStatsInput[],
  bytesUploaded: number,
  bytesSamples: UploadBytesSample[],
  nowMs: number,
): UploadStats | null {
  let totalRemainingBytes = 0;
  let anyStarted = false;

  for (const f of files) {
    const fileSize = f.file?.size || 0;
    if (fileSize === 0) continue;
    totalRemainingBytes += (1 - f.progress / 100) * fileSize;
    if (f.uploadStartedAt != null || f.progressSamples?.length) anyStarted = true;
  }

  if (!anyStarted) return null;

  const anchor = findWindowAnchor(bytesSamples, (p) => p.bytes, bytesUploaded, nowMs);
  let speedBps = 0;
  if (anchor) {
    const elapsedSec = (nowMs - anchor.t) / 1000;
    if (elapsedSec > 0) speedBps = Math.max(0, bytesUploaded - anchor.bytes) / elapsedSec;
  }

  return {
    speedBps,
    etaSeconds: speedBps > 0 ? totalRemainingBytes / speedBps : Infinity,
  };
}
